import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { extractFrame, checkTaskStatus } from '../../services/videoApi';
import { 
    Upload, Download, Loader2, AlertCircle, 
    CheckCircle, XCircle, FileUp, Image as ImageIcon, PlayCircle 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const VideoToImage = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [error, setError] = useState(null);
    
    // Video Ref to access current time
    const videoRef = useRef(null);
    
    const [isProcessing, setIsProcessing] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [progressStep, setProgressStep] = useState('');
    const [progressValue, setProgressValue] = useState(0);
    
    const [result, setResult] = useState(null);
    
    const pollingIntervalRef = useRef(null);
    const currentTimeRef = useRef(0);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [previewUrl]);

    // Polling logic
    useEffect(() => {
        if (taskId && isProcessing) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const data = await checkTaskStatus(taskId);
                    
                    if (data.state === 'SUCCESS' || data.status === 'SUCCESS') {
                        clearInterval(pollingIntervalRef.current);
                        const payload = data.result;
                        if (payload.new_credits !== undefined) setCredits(payload.new_credits);

                        if (payload.result) {
                            setResult({
                                downloadUrl: payload.result.download_url,
                                filename: payload.result.filename
                            });
                            setProgressValue(100);
                            setIsProcessing(false);
                        } else {
                            setError('Processing failed to return a file.');
                            setIsProcessing(false);
                        }
                    } else if (data.state === 'FAILURE' || data.status === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(data.error || 'Processing failed');
                        setIsProcessing(false);
                    } else if (data.info) {
                        if (data.info.step) setProgressStep(data.info.step);
                        if (data.info.progress) setProgressValue(data.info.progress);
                    }
                } catch (err) {
                    clearInterval(pollingIntervalRef.current);
                    setError(err.message);
                    setIsProcessing(false);
                }
            }, 2000);
        }
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [taskId, isProcessing, setCredits]);

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (selected) {
            if (selected.size > 50 * 1024 * 1024) {
                setError(t('videoImage.errors.tooLarge'));
                return;
            }
            setFile(selected);
            setFileName(selected.name);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(selected));
            setError(null);
            setResult(null);
            setProgressValue(0);
            setCurrentTime(0);
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            // Use a ref to avoid re-renders during playback
            currentTimeRef.current = videoRef.current.currentTime;
        }
    };

    const handleVideoStop = () => {
        if (videoRef.current) {
            const time = videoRef.current.currentTime;
            currentTimeRef.current = time; // Ensure ref is synced
            setCurrentTime(time);          // Trigger UI update
        }
    };

    const handleClear = () => {
        setFile(null);
        setFileName('');
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setError(null);
        setResult(null);
        setProgressValue(0);
        setCurrentTime(0);
        const fileInput = document.getElementById('video-upload-image');
        if (fileInput) fileInput.value = '';
    };

    const handleProcess = async () => {
        if (!file) return;
        
        setIsProcessing(true);
        setProgressValue(5);
        setError(null);
        setResult(null);
        
        // Use the current time from state (updated by video player)
        try {
            const response = await extractFrame(file, currentTimeRef.current);
            setTaskId(response.task_id);
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    // UI Logic
    const renderUploadView = () => (
        <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold mb-2 text-brand-orange">{t('videoImage.title')}</h2>
            <p className="mt-2 text-brand-dark">{t('videoImage.description')}</p>
            
            <div className="mt-8">
                <label className="block text-sm font-medium mb-2 text-brand-dark">{t('videoImage.uploadLabel')}</label>
                <div className="flex items-center p-4 border-2 border-dashed border-slate-400 rounded-md bg-slate-50">
                    <FileUp className="h-8 w-8 text-slate-500" />
                    <label htmlFor="video-upload-image" className="ml-4 text-sm text-brand-orange font-semibold cursor-pointer">
                        {t('videoImage.selectFile')}
                        <input 
                            id="video-upload-image" 
                            type="file" 
                            className="sr-only" 
                            onChange={handleFileChange} 
                            accept=".mp4,.avi,.mov,.mkv" 
                        />
                    </label>
                    {fileName && (
                        <div className="ml-auto flex items-center gap-2 text-sm text-slate-700">
                            <span className="truncate max-w-[200px]">{fileName}</span>
                            <button onClick={handleClear}><XCircle className="h-5 w-5 text-red-500" /></button>
                        </div>
                    )}
                </div>
                <p className="text-xs text-slate-500 mt-1">{t('videoImage.uploadHint')}</p>
                
                {error && (
                    <p className="mt-4 text-red-500 flex items-center"><AlertCircle size={16} className="mr-2"/>{error}</p>
                )}
            </div>

            {previewUrl && (
                <div className="mt-8">
                     <p className="text-sm text-slate-600 mb-2 font-medium">{t('videoImage.instruction')}</p>
                    <div className="rounded-lg overflow-hidden bg-black aspect-video flex items-center justify-center max-h-[500px]">
                        <video 
                            ref={videoRef}
                            src={previewUrl} 
                            controls 
                            onTimeUpdate={handleTimeUpdate}
                            onPause={handleVideoStop}       // Update UI number when paused
                            onSeeked={handleVideoStop}
                            className="w-full h-full object-contain" 
                        />
                    </div>
                    <div className="mt-4 p-4 bg-orange-50 rounded-md border border-orange-200 flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-2 text-brand-dark">
                            <PlayCircle className="text-brand-orange" />
                            <span className="font-semibold">{t('videoImage.currentTime')}: {currentTime.toFixed(2)}s</span>
                        </div>
                        
                        <button
                            onClick={handleProcess}
                            disabled={isProcessing}
                            className="w-full md:w-auto px-6 py-2 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {isProcessing ? (
                                <><Loader2 className="animate-spin mr-2" size={16} /> {t('videoImage.waitMessage')}</>
                            ) : (
                                <><ImageIcon className="mr-2" size={18}/> {t('videoImage.button')}</>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {isProcessing && (
                <div className="mt-6">
                    <div className="flex justify-between text-sm font-medium text-slate-700 mb-2">
                        <span>{t('videoImage.processing')}</span>
                        <span>{progressValue}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                        <div className="bg-brand-orange h-2.5 rounded-full transition-all duration-500" style={{ width: `${progressValue}%` }}></div>
                    </div>
                </div>
            )}
        </div>
    );

    const renderResultView = () => (
        <div className="max-w-4xl mx-auto">
            <div className="bg-green-50 border border-green-200 p-4 rounded-md mb-6 flex items-center text-green-800">
                <CheckCircle className="mr-2" /> {t('videoImage.success')}
            </div>

            <div className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden">
                <div className="p-4 bg-slate-50 border-b border-slate-200">
                    <h4 className="font-semibold text-lg text-brand-dark">{t('videoImage.resultTitle')}</h4>
                </div>
                
                <div className="p-4 flex flex-col items-center justify-center gap-6">
                    <img 
                        src={result.downloadUrl} 
                        alt="Extracted Frame" 
                        className="max-w-full max-h-[500px] rounded border border-slate-200 shadow-sm"
                    />

                    <a 
                        href={result.downloadUrl} 
                        download={result.filename}
                        className="flex items-center gap-2 bg-brand-orange text-white px-6 py-3 rounded-md font-semibold hover:bg-orange-600 transition shadow-sm"
                    >
                        <Download size={20} /> {t('videoImage.download')}
                    </a>
                </div>
            </div>
            
            <button onClick={handleClear} className="mt-6 text-slate-600 hover:text-brand-orange underline block w-full text-center">
                {t('videoImage.processAnother')}
            </button>
        </div>
    );

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
               <SubscribePrompt />
            ) : user?.credits < 1 ? (
               <PurchaseCreditsPrompt />
            ) : result ? (
                renderResultView()
            ) : (
                renderUploadView()
            )}
        </div>
    );
};

export default VideoToImage;
